const DEFAULT_PARENT_NAME = '—'

const CATEGORY_DETAIL_FIELDS = Object.freeze({
  name: Object.freeze({
    key: 'name',
    backendKey: 'name',
    label: 'Назва',
    inputType: 'text',
  }),
  code: Object.freeze({
    key: 'code',
    backendKey: 'code',
    label: 'Символьний код',
    inputType: 'text',
  }),
  active: Object.freeze({
    key: 'active',
    backendKey: 'active',
    label: 'Активна категорія',
    inputType: 'checkbox',
  }),
  parent: Object.freeze({
    key: 'parent',
    backendKey: 'parent',
    label: 'Батьківська категорія',
    inputType: 'select',
  }),
})

const CATEGORY_DETAIL_FIELD_LIST = Object.freeze(Object.values(CATEGORY_DETAIL_FIELDS))

function getDefaultFieldValue(field) {
  if (field.inputType === 'checkbox') return false
  return ''
}

function normalizeFieldValue(field, value) {
  if (field.inputType === 'checkbox') return !!value
  if (field.inputType === 'select') return CategoryEntity.toId(value)
  return value === null || value === undefined ? '' : String(value)
}

export default class CategoryEntity {
  constructor(data = {}) {
    this.id = CategoryEntity.toId(data.id)
    this.parentName = typeof data.parentName === 'string' && data.parentName
      ? data.parentName
      : DEFAULT_PARENT_NAME

    CATEGORY_DETAIL_FIELD_LIST.forEach((field) => {
      const rawValue = data[field.key]
      this[field.key] = normalizeFieldValue(
        field,
        rawValue === undefined ? getDefaultFieldValue(field) : rawValue,
      )
    })
  }

  static toId(value) {
    if (typeof value === 'string' || typeof value === 'number') return String(value)
    return ''
  }

  static getIdFromRaw(category = {}) {
    return CategoryEntity.toId(category?._id ?? category?.id ?? category?._id?.$oid)
  }

  static getNameFromRaw(category = {}) {
    return typeof category?.name === 'string' ? category.name : ''
  }

  static getParentIdFromRaw(category = {}) {
    return CategoryEntity.toId(category?.parent?._id ?? category?.parent?.id ?? category?.parent)
  }

  static getParentNameFromRaw(category = {}) {
    return typeof category?.parent?.name === 'string' ? category.parent.name : ''
  }

  static fromBackend(category = {}, options = {}) {
    if (category instanceof CategoryEntity) return category

    const id = CategoryEntity.getIdFromRaw(category)
    const parentId = CategoryEntity.getParentIdFromRaw(category)
    const directParentName = CategoryEntity.getParentNameFromRaw(category)
    const parentNameFromMap = options.parentNameById?.get(parentId) || ''
    const parentName = directParentName || (parentId ? parentNameFromMap || DEFAULT_PARENT_NAME : DEFAULT_PARENT_NAME)

    const entityData = {
      id,
      parentName,
    }

    CATEGORY_DETAIL_FIELD_LIST.forEach((field) => {
      if (field.key === 'parent') {
        entityData[field.key] = parentId || ''
        return
      }
      entityData[field.key] = category?.[field.backendKey]
    })

    return new CategoryEntity(entityData)
  }

  static fromBackendCollection(items = []) {
    const source = Array.isArray(items) ? items : []
    const parentNameById = new Map(
      source
        .map((item) => [CategoryEntity.getIdFromRaw(item), CategoryEntity.getNameFromRaw(item)])
        .filter(([itemId, itemName]) => itemId && itemName),
    )

    return source.map((item) => CategoryEntity.fromBackend(item, { parentNameById }))
  }

  static fromFormData(category = {}) {
    if (category instanceof CategoryEntity) return category

    const entityData = {
      id: CategoryEntity.toId(category.id),
      parentName: DEFAULT_PARENT_NAME,
    }

    CATEGORY_DETAIL_FIELD_LIST.forEach((field) => {
      entityData[field.key] = category[field.key]
    })

    return new CategoryEntity(entityData)
  }

  static toBackendPayload(category = {}) {
    const entity = CategoryEntity.fromFormData(category)
    const payload = {}

    CATEGORY_DETAIL_FIELD_LIST.forEach((field) => {
      const fieldValue = entity[field.key]
      payload[field.backendKey] = field.key === 'parent' ? (fieldValue || null) : fieldValue
    })

    return payload
  }

  static getDetailFields() {
    return CATEGORY_DETAIL_FIELDS
  }

  static getDetailFieldList() {
    return CATEGORY_DETAIL_FIELD_LIST
  }

  static createEmptyDetailForm() {
    const form = {}
    CATEGORY_DETAIL_FIELD_LIST.forEach((field) => {
      form[field.key] = getDefaultFieldValue(field)
    })
    return form
  }
}
